import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import {
  setDataByNodeId,
  deleteConditionNode,
  addConditionNode,
} from '../../../features/mainSlice';
import { openModal } from '../../../features/modalSlice';
import { color } from '../../../app/theme';
import { NodeType, ModalType } from '../../../app/types';
import IconAddCircle from '../../../assets/images/Icons/IconAddCircle';
import isMinus from '../../../assets/images/svg/ic-minus-circle.svg';
import IcHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import isSun from '../../../assets/images/svg/ic-sun.svg';
import SelectButtonTextField from './SelectButtonTextField';
import Tooltip from '../../shared/Tooltip';
import AllowMulti from './AllowMulti';
import config from '../../../app/config';

const useStyles = makeStyles(
  {
    root: {
      marginTop: 16,
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'end',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
      '& > div': {
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: '1',
        letterSpacing: 'normal',
        color: color.$12,
      },
    },
    conditionBlock: {
      backgroundColor: color.$19,
      padding: 16,
      borderRadius: 24,
      marginBottom: 12,
    },
    minus: {
      marginLeft: 8,
      cursor: 'pointer',
    },
    conditionTitle: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$12,
      marginBottom: 12,
      '& > img': { marginLeft: 4 },
    },
    red: {
      color: `${color.$17} !important`,
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      color: color.$2,
      fontSize: 15,
      lineHeight: 1,
      cursor: 'pointer',
      '&:hover': {
        opacity: '0.6',
      },
      '& > svg': {
        marginRight: 4,
      },
    },
    conditionRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
      '& > div': { width: '100%' },
    },
    sun: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$7,
      background: color.$15,
      width: 80,
      borderRadius: '20px',
      justifyContent: 'center',
      padding: '2px 0',
      marginBottom: 6,
    },
    hint: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: color.$13,
      marginBottom: 24,
    },
  },
  {
    name: 'Branch',
  },
);

const Branch = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data, selectedNode, isErrorNodeIds } = useSelector(state => state.ma);
  const { nodes = [] } = data || {};
  const { source } = selectedNode?.data;
  const sourceNode = nodes.find(n => n?.id === source);
  const groupId = selectedNode?.id;

  const isError = !!~isErrorNodeIds.indexOf(groupId);

  const endCount = useMemo(() => {
    return (nodes || []).filter(n => n?.type === NodeType.END).length || 0;
  }, [nodes.length]);
  const isMaxEndNodVerify = endCount < config.MAX_END_COUNT;

  const conditionNodes = nodes.filter(
    n =>
      n?.type === NodeType.CONDITION &&
      n?.data?.groupId === groupId &&
      n?.data?.type === 'click',
  );

  const allSelectButtons = useMemo(() => {
    let res = [];
    conditionNodes.forEach(c => {
      res = [...res, ...c?.data?.buttons];
    });

    return res;
  }, [conditionNodes]);

  const menuItems = useMemo(() => {
    const { messages } = sourceNode?.data || [];
    const items = [];
    let index = 1;

    messages.forEach(message => {
      if (message?.contentType === 'application/x-template') {
        (message?.data?.elements || []).forEach((element, elementIndex) => {
          const { buttons } = element || {};
          buttons.forEach((button, buttonIndex) => {
            const isPhone = button?.type === 'phone';
            const isNotHttpUrl =
              button?.type === 'url' && !/(http(s?)):\/\//i.test(button?.data);
            const isLocation = button?.type === 'location';
            if (!isPhone && !isNotHttpUrl && !isLocation) {
              const buttonId = `${message?.template}_elements.${elementIndex}.buttons.${buttonIndex}`;
              items.push({
                index,
                id: buttonId,
              });
            }
            index += 1;
          });
        });
      }
    });

    return items;
  }, [sourceNode]);

  const onEnter = (node, item) => {
    dispatch(
      setDataByNodeId(node?.id, {
        buttons: [...node?.data?.buttons, item],
      }),
    );
  };

  const onDelete = (node, index) => {
    dispatch(
      setDataByNodeId(node?.id, {
        buttons: node?.data?.buttons.filter(b => b?.index !== index),
      }),
    );
  };

  const onAddCondition = () => {
    dispatch(addConditionNode(groupId));
  };

  const onDeleteCondition = node => {
    dispatch(
      openModal({
        modalType: ModalType.DIALOG_TEXT,
        modalProps: {
          title: t('delete-branch-modal.title'),
          content: t('delete-branch-modal.content'),
          cancelText: t('delete-branch-modal.cancelText'),
          submitText: t('delete-branch-modal.submitText'),
        },
        onClick: () => {
          dispatch(deleteConditionNode(node?.id));
        },
      }),
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        {t('condition-setting.branch')}
        <div
          className={classnames({
            [classes.red]: !isMaxEndNodVerify,
          })}
        >
          {t('condition-setting.over-branch-num', {
            num: config.MAX_END_COUNT - endCount,
          })}
        </div>
      </div>
      <div className={classes.conditionBlock}>
        <div
          className={classnames(classes.conditionTitle, {
            [classes.red]: menuItems.length - allSelectButtons.length > 0,
          })}
        >
          {t('condition-setting.over-button-num', {
            num: menuItems.length - allSelectButtons.length,
          })}
          <Tooltip
            light
            placement="top"
            title={t('condition-setting.over-button-tooltip')}
          >
            <img
              className={classes.marginLeft8}
              src={IcHelpCircle}
              alt="alert"
            />
          </Tooltip>
        </div>
        {conditionNodes.map(conditionNode => (
          <div
            key={`conditionNode-${conditionNode?.id}`}
            className={classes.conditionRow}
          >
            <SelectButtonTextField
              placeholder={t('condition-setting.over-button-placeholder')}
              menuItems={menuItems || []}
              buttons={conditionNode?.data?.buttons}
              onEnter={item => onEnter(conditionNode, item)}
              onDelete={index => onDelete(conditionNode, index)}
              allSelect={allSelectButtons}
              isError={isError && !(conditionNode?.data?.buttons || []).length}
            />
            {conditionNodes.length > 1 && (
              <img
                className={classes.minus}
                onClick={() => onDeleteCondition(conditionNode)}
                src={isMinus}
                alt="minus"
              />
            )}
          </div>
        ))}
        {isMaxEndNodVerify && (
          <div className={classes.icon} onClick={onAddCondition}>
            <IconAddCircle id="icon-add-message-type" width={20} height={20} />
            {t('condition-setting.add-button')}
          </div>
        )}
      </div>
      <AllowMulti />
      <div className={classes.sun}>
        <img src={isSun} alt="minus" />
        {t('trigger-setting.sun')}
      </div>
      <div className={classes.hint}>
        {t('condition-setting.over-button-hint')}
      </div>
    </div>
  );
};

export default Branch;
